import { dayText } from "./accounts";
import type { CycleBoard, CycleCapacity, CycleStop, RotationCalendar } from "./api";

export const STOPS_PER_HOUR = 1.75;
export const TRUCK_DAY_HOURS = 8;
export const TRUCKS_MAX = 6;

const round = (value: number, places: number) => Math.round(value * 10 ** places) / 10 ** places;

/** Design 8.7 step 3, the same as app/cycle.py: the capacity check of the selected stops. */
export function capacity(gallons: number[], truckSizes: number[]): CycleCapacity {
  const stops = gallons.length;
  const total = gallons.reduce((sum, value) => sum + value, 0);
  const hours = stops / STOPS_PER_HOUR;
  const days = hours / TRUCK_DAY_HOURS;
  const perDay = days ? total / days : 0;
  return {
    stops,
    gallons: round(total, 1),
    gal_per_stop: stops ? round(total / stops, 1) : 0,
    hours: round(hours, 1),
    truck_days: round(days, 2),
    gal_per_truck_day: Math.round(perDay),
    trucks: [...truckSizes].sort((a, b) => a - b).map((size) => ({ size_gal: size, fits: perDay <= size })),
  };
}

/** The board with one stop chosen or not, before the server answers. */
export function withChoice(board: CycleBoard, accountId: string, selected: boolean): CycleBoard {
  const stops = board.stops.map((stop) => (stop.account_id === accountId ? { ...stop, selected } : stop));
  const chosen = (rows: CycleStop[]) => rows.filter((stop) => stop.selected).map((stop) => stop.estimated_gal);
  return {
    ...board,
    stops,
    capacity: capacity(chosen(stops), board.truck_sizes),
    areas: board.areas.map((area) => ({
      ...area,
      capacity: capacity(
        chosen(stops.filter((stop) => stop.area === area.area)),
        board.truck_sizes,
      ),
    })),
  };
}

export function weekText(board: Pick<CycleBoard, "week" | "week_start" | "week_end">): string {
  return `Rotation week ${board.week} · ${dayText(board.week_start)} to ${dayText(board.week_end)}`;
}

export function truckDaysText(days: number): string {
  return `${days} truck-${days === 1 ? "day" : "days"}`;
}

const WEEKS = 4;
const DAY_MS = 86_400_000;

function addDays(iso: string, days: number): string {
  return new Date(Date.parse(`${iso}T12:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);
}

/** "Oct 13–19", or "Oct 27–Nov 2" when the week ends in the next month. */
export function rangeText(start: string, end: string): string {
  const sameMonth = start.slice(0, 7) === end.slice(0, 7);
  return `${dayText(start)}–${sameMonth ? Number(end.slice(8, 10)) : dayText(end)}`;
}

export interface WeekTab {
  week: number;
  start: string;
  end: string;
  label: string;
}

/** The four rotation weeks in date order, from this week on, labelled with their workdays (rule A-8). */
export function weekTabs(board: Pick<CycleBoard, "week" | "week_start" | "this_week">, holidays: string[] = []): WeekTab[] {
  const thisStart = addDays(board.week_start, -7 * ((board.week - board.this_week + WEEKS) % WEEKS));
  return Array.from({ length: WEEKS }, (_, ahead) => {
    const week = ((board.this_week - 1 + ahead) % WEEKS) + 1;
    const start = addDays(thisStart, 7 * ahead);
    const end = addDays(start, 6);
    const days = workdays(start, holidays);
    const when = ahead === 0 ? "Now" : ahead === 1 ? "next week" : `in ${ahead} weeks`;
    return { week, start, end, label: `${days ? rangeText(days.first, days.last) : rangeText(start, end)} · week ${week} · ${when}` };
  });
}

/** The first and the last workday of a week: Monday to Friday, less the holidays. */
export function workdays(start: string, holidays: string[]): { first: string; last: string } | null {
  const off = new Set(holidays);
  const days = Array.from({ length: 7 }, (_, n) => addDays(start, n)).filter((day) => new Date(`${day}T12:00:00Z`).getUTCDay() % 6 !== 0 && !off.has(day));
  return days.length ? { first: days[0], last: days[days.length - 1] } : null;
}

/** Rule A-8: the week filter names the workdays of each rotation week, "Week 3 (Nov 2–6)", and "(Now)" for this week. */
export function weekOptions(rotation: RotationCalendar | undefined): { week: number; text: string }[] {
  if (!rotation) return [1, 2, 3, 4].map((week) => ({ week, text: `Week ${week}` }));
  const tabs = weekTabs({ week: rotation.this_week, week_start: rotation.week_start, this_week: rotation.this_week }, rotation.holidays ?? []);
  return tabs.map((tab, ahead) => {
    const days = workdays(tab.start, rotation.holidays ?? []);
    const when = ahead === 0 ? "Now" : days ? rangeText(days.first, days.last) : rangeText(tab.start, tab.end);
    return { week: tab.week, text: `Week ${tab.week} (${when})` };
  });
}

/** The week in the address, when it is a rotation week. */
export function weekFromSearch(search: string): number | undefined {
  const value = Number(new URLSearchParams(search).get("week"));
  return Number.isInteger(value) && value >= 1 && value <= WEEKS ? value : undefined;
}

export function pickedUpText(stop: Pick<CycleStop, "picked_up">): string | null {
  return stop.picked_up ? `Picked up ${dayText(stop.picked_up)}` : null;
}

const THOUSANDS = /^\d{1,3}(,\d{3})+$/;

/** Split the sizes on spaces and commas, but keep "1,200" as one number. */
export function sizeParts(text: string): string[] {
  return text
    .trim()
    .split(/\s+|,\s+|;/)
    .filter(Boolean)
    .flatMap((token) => {
      const joined = token.replace(/,/g, "");
      if (THOUSANDS.test(token) && Number(joined) >= 100 && Number(joined) <= 5000) return [joined];
      return token.split(",").filter(Boolean);
    });
}

/** Read the settings form; give the error text, or the sizes sorted. */
export function trucksChange(text: string): number[] | string {
  const parts = sizeParts(text);
  if (parts.length === 0) return "Give at least one size.";
  if (parts.length > TRUCKS_MAX) return `Give ${TRUCKS_MAX} sizes at most.`;
  if (parts.some((part) => !/^\d+$/.test(part))) return "Give each size in whole gallons.";
  const found = parts.map(Number).sort((a, b) => a - b);
  if (found.some((size) => size < 100 || size > 5000)) return "Each size must be from 100 to 5000 gal.";
  if (new Set(found).size !== found.length) return "Give each size one time.";
  return found;
}

/** The first day of the week that routes can still use: the week start, or today in this week. */
export const routesDay = (weekStart: string, today: string) => (weekStart > today ? weekStart : today);
